import { DiscountValueType, DiscountBandTypeEnum } from 'app/shared/model';
import type { EditableDiscountBand } from 'app/pages/DiscountEditor/modules/DiscountActions/state';

export const DISCOUNT_REQUIRED_FIELD_MESSAGE = 'This is a required field';

export const DISCOUNT_MAX_MIN_UNITS = 9999;
export const DISCOUNT_MAX_MONETARY = 9_999_999_999.99999;
export const DISCOUNT_MAX_MONETARY_SCALED = 9999999999n * 100000n + 99999n;
export const DISCOUNT_MONETARY_DECIMAL_PLACES = 5;
export const DISCOUNT_PERCENT_DECIMAL_PLACES = 2;

export const DISCOUNT_ERR_MIN_UNITS_MAX = 'Value cannot exceed 9,999';
export const DISCOUNT_ERR_MIN_UNITS_WHOLE = 'Whole numbers only (no decimals allowed)';
export const DISCOUNT_ERR_MONETARY_MAX = 'Value cannot exceed 9,999,999,999.99999';
export const DISCOUNT_ERR_DECIMAL_PLACES = 'Maximum 5 decimal places allowed';
export const DISCOUNT_ERR_PERCENT_DECIMAL_PLACES = 'Maximum 2 decimal places allowed';

export const DISCOUNT_MONETARY_INPUT_REGEX = new RegExp(
    `^[0-9]*(\\.[0-9]{0,${DISCOUNT_MONETARY_DECIMAL_PLACES}})?$`
);

const DISCOUNT_MONETARY_INPUT_MAX_INT_DIGITS = 14;
export function clampMonetaryDiscountInput(raw: string): string {
    if (raw === '') return '';
    const s = raw.replace(/[^\d.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot === -1) {
        return s.length > DISCOUNT_MONETARY_INPUT_MAX_INT_DIGITS
            ? s.slice(0, DISCOUNT_MONETARY_INPUT_MAX_INT_DIGITS)
            : s;
    }
    const intPart = s.slice(0, firstDot);
    const intClamped =
        intPart.length > DISCOUNT_MONETARY_INPUT_MAX_INT_DIGITS
            ? intPart.slice(0, DISCOUNT_MONETARY_INPUT_MAX_INT_DIGITS)
            : intPart;
    const afterDot = s.slice(firstDot + 1);
    const digitsAfter = afterDot.replace(/\D/g, '');
    if (digitsAfter.length === 0 && s.endsWith('.')) {
        return intClamped + '.';
    }
    const fracPart = digitsAfter.slice(0, DISCOUNT_MONETARY_DECIMAL_PLACES);
    return intClamped + '.' + fracPart;
}

export function formatSavedMonetaryDiscountForInput(n: number): string {
    if (!Number.isFinite(n)) return '';
    const fixed = n.toFixed(DISCOUNT_MONETARY_DECIMAL_PLACES);
    const trimmed = fixed.replace(/\.?0+$/, '');
    return trimmed === '' ? '0' : trimmed;
}

export function formatSavedPercentDiscountForInput(n: number): string {
    if (!Number.isFinite(n)) return '';
    const capped = Math.min(100, n);
    const fixed = capped.toFixed(DISCOUNT_PERCENT_DECIMAL_PLACES);
    const trimmed = fixed.replace(/\.?0+$/, '');
    return trimmed === '' ? '0' : trimmed;
}

export const DISCOUNT_PERCENT_INPUT_REGEX = new RegExp(
    `^[0-9]*(\\.[0-9]{0,${DISCOUNT_PERCENT_DECIMAL_PLACES}})?$`
);

function clampPercentIntegerSegment(intDigits: string): string {
    if (intDigits === '') return '';
    let t = intDigits.length > 3 ? intDigits.slice(0, 3) : intDigits;
    if (t.length === 3 && t !== '100') {
        t = t.slice(0, 2);
    }
    return t;
}

export function clampPercentDiscountInput(raw: string): string {
    if (raw === '') return '';
    const s = raw.replace(/[^\d.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot === -1) {
        return clampPercentIntegerSegment(s);
    }
    const intClamped = clampPercentIntegerSegment(s.slice(0, firstDot));
    const afterDot = s.slice(firstDot + 1);
    const digitsAfter = afterDot.replace(/\D/g, '');
    if (digitsAfter.length === 0 && s.endsWith('.')) {
        return intClamped + '.';
    }
    const fracPart = digitsAfter.slice(0, DISCOUNT_PERCENT_DECIMAL_PLACES);
    const combined = intClamped + '.' + fracPart;
    const val = parseFloat(combined);
    if (Number.isFinite(val) && val > 100) {
        return '100';
    }
    return combined;
}

export function isMonetaryDiscountValueType(t: DiscountValueType): boolean {
    return t === DiscountValueType.AmountPerUnit || t === DiscountValueType.OverrideUnitPrice;
}

export function shouldShowMonetaryDiscountValueMessageInline(
    showValidation: boolean,
    valueType: DiscountValueType,
    message: string | undefined
): boolean {
    if (showValidation) return true;
    return isMonetaryDiscountValueType(valueType) && message === DISCOUNT_ERR_MONETARY_MAX;
}

function parseMonetaryStringToScaled(s: string): bigint | undefined {
    if (!DISCOUNT_MONETARY_INPUT_REGEX.test(s)) return undefined;
    const dot = s.indexOf('.');
    const intStrRaw = dot === -1 ? s : s.slice(0, dot);
    const fracStrRaw = dot === -1 ? '' : s.slice(dot + 1);
    const intNormalized = intStrRaw.replace(/^0+/, '') || '0';
    const intPart = BigInt(intNormalized);
    const fracPadded = (fracStrRaw + '00000').slice(0, DISCOUNT_MONETARY_DECIMAL_PLACES);
    const fracPart = fracStrRaw.length === 0 ? 0n : BigInt(fracPadded);
    return intPart * 100000n + fracPart;
}

export function normalizeMonetaryDiscountOnBlur(raw: string): string {
    if (raw === '') return raw;
    const t = raw.trim();
    const dot = t.indexOf('.');
    if (dot === -1) {
        const intTrimmed = t.replace(/^0+/, '') || '0';
        return intTrimmed;
    }
    let intPart = t.slice(0, dot).replace(/^0+/, '') || '0';
    let fracPart = t
        .slice(dot + 1)
        .replace(/\D/g, '')
        .slice(0, DISCOUNT_MONETARY_DECIMAL_PLACES);
    fracPart = fracPart.replace(/0+$/, '');
    if (fracPart === '') return intPart;
    return `${intPart}.${fracPart}`;
}

export function monetaryDiscountStringToApiNumber(valueType: DiscountValueType, value: string): number {
    if (isMonetaryDiscountValueType(valueType)) {
        const scaled = parseMonetaryStringToScaled(value);
        if (scaled !== undefined) {
            return Number(scaled) / 100000;
        }
    }
    return parseFloat(value);
}

export function normalizePercentDiscountOnBlur(raw: string): string {
    if (raw === '') return raw;
    let n = parseFloat(raw);
    if (!Number.isFinite(n)) return raw;
    if (n > 100) n = 100;
    const fixed = n.toFixed(DISCOUNT_PERCENT_DECIMAL_PLACES);
    const trimmed = fixed.replace(/\.?0+$/, '');
    return trimmed === '' ? '0' : trimmed;
}

export function monetaryAmountKeyPart(value: string): string | undefined {
    if (value === '') return undefined;
    const scaled = parseMonetaryStringToScaled(value);
    if (scaled === undefined) return undefined;
    const intPart = scaled / 100000n;
    const frac = scaled % 100000n;
    return `${intPart.toString()}.${frac.toString().padStart(DISCOUNT_MONETARY_DECIMAL_PLACES, '0')}`;
}

export function validateDiscreteMinUnits(from: string): string | undefined {
    if (from === '') return undefined;
    if (!/^[0-9]+$/.test(from)) {
        return DISCOUNT_ERR_MIN_UNITS_WHOLE;
    }
    const n = parseInt(from, 10);
    if (n > DISCOUNT_MAX_MIN_UNITS) return DISCOUNT_ERR_MIN_UNITS_MAX;
    return undefined;
}

export function validateMonetaryDiscountValue(value: string): string | undefined {
    if (value === '') return undefined;
    if (!DISCOUNT_MONETARY_INPUT_REGEX.test(value)) return DISCOUNT_ERR_DECIMAL_PLACES;
    const scaled = parseMonetaryStringToScaled(value);
    if (scaled === undefined) return DISCOUNT_ERR_DECIMAL_PLACES;
    if (scaled > DISCOUNT_MAX_MONETARY_SCALED) return DISCOUNT_ERR_MONETARY_MAX;
    return undefined;
}

export function validatePercentDiscountValue(value: string): string | undefined {
    if (value === '') return undefined;
    if (!DISCOUNT_PERCENT_INPUT_REGEX.test(value)) return DISCOUNT_ERR_PERCENT_DECIMAL_PLACES;
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return DISCOUNT_ERR_PERCENT_DECIMAL_PLACES;
    if (n > 100) return 'Cannot discount more than 100%';
    return undefined;
}

function discreteFromFieldMessage(from: string): string {
    if (from === '') return DISCOUNT_REQUIRED_FIELD_MESSAGE;
    return validateDiscreteMinUnits(from) ?? '';
}

function discreteValueFieldMessage(value: string, valueType: DiscountValueType): string {
    if (value === '') return DISCOUNT_REQUIRED_FIELD_MESSAGE;
    if (valueType === DiscountValueType.Percent) {
        return validatePercentDiscountValue(value) ?? '';
    }
    if (isMonetaryDiscountValueType(valueType)) {
        return validateMonetaryDiscountValue(value) ?? '';
    }
    return '';
}

export function revalidateDiscreteEditableBand(
    band: EditableDiscountBand,
    discountValueType: DiscountValueType
): EditableDiscountBand {
    const fromMessage = discreteFromFieldMessage(band.from);
    const valueMessage = discreteValueFieldMessage(band.value, discountValueType);
    return {
        ...band,
        fromMessage: fromMessage || undefined,
        valueMessage: valueMessage || undefined,
        to: '',
        toMessage: undefined,
        isValid: !fromMessage && !valueMessage,
    };
}

export function revalidateEditableDiscountBand(
    band: EditableDiscountBand,
    discountValueType: DiscountValueType,
    bandType: DiscountBandTypeEnum
): EditableDiscountBand {
    if (bandType === DiscountBandTypeEnum.Discrete) {
        return revalidateDiscreteEditableBand(band, discountValueType);
    }

    let fromMessage: string | undefined;
    let toMessage: string | undefined;
    let valueMessage: string | undefined;
    let isValid = true;

    if (band.from === '') {
        fromMessage = DISCOUNT_REQUIRED_FIELD_MESSAGE;
        isValid = false;
    } else {
        const fe = validateDiscreteMinUnits(band.from);
        if (fe) {
            fromMessage = fe;
            isValid = false;
        }
    }
    if (band.to === '') {
        toMessage = DISCOUNT_REQUIRED_FIELD_MESSAGE;
        isValid = false;
    } else {
        const te = validateDiscreteMinUnits(band.to);
        if (te) {
            toMessage = te;
            isValid = false;
        }
    }
    if (band.value === '') {
        valueMessage = DISCOUNT_REQUIRED_FIELD_MESSAGE;
        isValid = false;
    } else if (discountValueType === DiscountValueType.Percent) {
        const pe = validatePercentDiscountValue(band.value);
        if (pe) {
            valueMessage = pe;
            isValid = false;
        }
    } else if (isMonetaryDiscountValueType(discountValueType)) {
        const me = validateMonetaryDiscountValue(band.value);
        if (me) {
            valueMessage = me;
            isValid = false;
        }
    }
    if (band.from !== '' && band.to !== '') {
        const fromNum = parseInt(band.from, 10);
        const toNum = parseInt(band.to, 10);
        if (
            Number.isFinite(fromNum) &&
            Number.isFinite(toNum) &&
            fromNum <= DISCOUNT_MAX_MIN_UNITS &&
            toNum <= DISCOUNT_MAX_MIN_UNITS &&
            fromNum > toNum
        ) {
            isValid = false;
            toMessage = 'Value must be ' + band.from + ' or more';
        }
    }

    return {
        ...band,
        fromMessage,
        toMessage,
        valueMessage,
        isValid,
    };
}